/**
 * Renderer-side orchestration for multi-source connection import: listing
 * sources, scanning one (with an e2e localStorage seam), opening the review
 * dialog, and saving the selected candidates. Nothing auto-connects — import
 * only saves.
 */
import type {
  ImportCandidate,
  ImportScanResult,
  ImportSourceCount,
  ImportSourceId,
  ImportSourceInfo,
} from '../../../types/connectionImport';
import { useConnectionStore } from '../store/connectionStore';
import { useConnectionImportStore, type ImportEntry } from './connectionImportStore';
import { isFileOnlySource } from './connectionImportSources';
import { buildConnectionFromCandidate, countBucket } from './connectionImportUtils';
import type { AzureDiscoveredResource } from '../../../types/azureDiscovery';
import { azureResourcesToScanResult } from './azureImportMapping';

const emptyResult = (sourceId: ImportSourceId): ImportScanResult => ({
  sourceId,
  found: false,
  candidates: [],
});

/**
 * E2E seam: when running under test mode a fixture path may be stashed in
 * localStorage per source (`sql-studio-e2e-import-path-<sourceId>`); the scan
 * routes through `scanFile` for that path instead of OS auto-detection. No
 * effect in production (the key is never set).
 */
function testOverridePath(sourceId: ImportSourceId): string | null {
  try {
    if (typeof window === 'undefined') return null;
    if (window.electronAPI?.testMode?.isTestMode?.() !== true) return null;
    return window.localStorage.getItem(`sql-studio-e2e-import-path-${sourceId}`);
  } catch {
    return null;
  }
}

export async function listImportSources(): Promise<ImportSourceInfo[]> {
  const api = window.electronAPI?.connectionImport;
  if (!api) return [];
  try {
    return await api.list();
  } catch {
    return [];
  }
}

/**
 * How many importable connections each given source holds — the number in the
 * empty-state "Found N connections in …" card. Counting is a real scan (unlike
 * detection), so callers pass only the sources they already know are detected.
 * Never throws: an unavailable API or a failed scan simply counts nothing.
 */
export async function countImportSources(
  sourceIds: ImportSourceId[]
): Promise<ImportSourceCount[]> {
  const api = window.electronAPI?.connectionImport;
  if (!api || sourceIds.length === 0) return [];
  try {
    // E2E seam: a source pointed at a fixture path counts through that file,
    // the same way `runScan` routes around OS auto-detection under test mode.
    const fixtureBacked = sourceIds.filter((id) => testOverridePath(id));
    const autoDetected = sourceIds.filter((id) => !testOverridePath(id));
    const [counted, fromFixtures] = await Promise.all([
      autoDetected.length > 0 ? api.count({ sourceIds: autoDetected }) : Promise.resolve([]),
      Promise.all(
        fixtureBacked.map(async (sourceId) => ({
          sourceId,
          count: (await runScan(sourceId)).candidates.filter((c) => c.supported).length,
        }))
      ),
    ]);
    return [...counted, ...fromFixtures];
  } catch {
    return [];
  }
}

export async function runScan(sourceId: ImportSourceId): Promise<ImportScanResult> {
  const api = window.electronAPI?.connectionImport;
  if (!api) return emptyResult(sourceId);
  const override = testOverridePath(sourceId);
  if (override) return api.scanFile({ sourceId, filePath: override });
  return api.scan({ sourceId });
}

export async function runScanFile(
  sourceId: ImportSourceId,
  filePath?: string
): Promise<ImportScanResult> {
  const api = window.electronAPI?.connectionImport;
  if (!api) return emptyResult(sourceId);
  // E2E seam: with no explicit path (the file-only Browse… flow), honor a
  // stashed fixture path instead of opening the un-drivable native picker.
  // No effect in production — the override only resolves in test mode.
  const target = filePath ?? testOverridePath(sourceId) ?? undefined;
  return api.scanFile({ sourceId, filePath: target });
}

/** Scan a source then open the review dialog (used by menu + palette + empty state). */
export async function openImportDialog(
  sourceId: ImportSourceId,
  entry: ImportEntry
): Promise<void> {
  const scanResult = await runScan(sourceId);
  useConnectionImportStore.getState().openFromScan(scanResult, sourceId, entry);
}

/**
 * Open the review dialog straight into Browse… for a file-only source (no
 * auto-detect). Opens the OS picker first; a cancel is a no-op.
 */
export async function openImportDialogViaBrowse(
  sourceId: ImportSourceId,
  entry: ImportEntry
): Promise<void> {
  const scanResult = await runScanFile(sourceId);
  if (scanResult.cancelled) return;
  useConnectionImportStore.getState().openFromScan(scanResult, sourceId, entry);
}

/**
 * Open the shared Azure resource picker for the import flow. Azure has no file
 * to scan — the picker discovers the signed-in account live, and confirming a
 * multi-select pick feeds the normal review dialog (see `AzureImportPicker`).
 */
export function openAzureImportPicker(entry: ImportEntry): void {
  useConnectionImportStore.getState().openAzurePicker(entry);
}

/**
 * Confirmed Browse Azure picks → the normal import review (mapped to candidates): the import source's multi-select
 * confirm, and the New Connection dialog's "Review N connections…" (C3.1), which opens it on top of the dialog — the
 * review then completes through the dialog host with `via: 'azure_review'`.
 */
export function openAzureReview(picked: AzureDiscoveredResource[], entry: ImportEntry): void {
  useConnectionImportStore.getState().openFromScan(azureResourcesToScanResult(picked), 'azure', entry);
}

/**
 * Route a picked source into its import flow — shared by the Import… menu and
 * the start-page discovery card's direct source buttons: Azure is discovered
 * live (picker), file-only sources jump straight to Browse…, everything else
 * scans and opens the review dialog.
 */
export function openImportSource(sourceId: ImportSourceId, entry: ImportEntry): void {
  if (sourceId === 'azure') openAzureImportPicker(entry);
  else if (isFileOnlySource(sourceId)) void openImportDialogViaBrowse(sourceId, entry);
  else void openImportDialog(sourceId, entry);
}

export interface ImportSelection {
  candidate: ImportCandidate;
  password?: string;
}

/**
 * Save the selected candidates as connections (never connects). Fires the
 * `feature:connection_import` success event afterwards. Returns the imported
 * count.
 */
export async function importSelectedCandidates(
  selections: ImportSelection[],
  sourceId: ImportSourceId,
  entry: ImportEntry
): Promise<number> {
  const { addConnection } = useConnectionStore.getState();
  let imported = 0;
  let includedWindowsAuth = false;

  for (const { candidate, password } of selections) {
    if (!candidate.supported) continue;
    if (candidate.windowsAuthUnavailable) includedWindowsAuth = true;
    const connection = buildConnectionFromCandidate(candidate, password);
    const ok = await addConnection(connection);
    if (ok) imported += 1;
  }

  if (imported > 0) {
    window.electronAPI?.analytics
      ?.track('feature:connection_import', {
        source: sourceId,
        imported_bucket: countBucket(imported),
        skipped_windows_auth: includedWindowsAuth,
        entry,
      })
      ?.catch(() => {});
  }

  return imported;
}
